import type { DropdownValue, NestedGroupItem } from '../../types';

export function buildSelectableByValue(items: NestedGroupItem[]): Map<DropdownValue, boolean> {
  const selectableByValue = new Map<DropdownValue, boolean>();

  const visit = (item: NestedGroupItem): void => {
    selectableByValue.set(item.value, item.type === 'value');

    for (const child of item.children) {
      visit(child);
    }
  };

  for (const item of items) {
    visit(item);
  }

  return selectableByValue;
}

export function buildDescendantsByValue(
  items: NestedGroupItem[],
  selectableByValue?: Map<DropdownValue, boolean>,
): Map<DropdownValue, DropdownValue[]> {
  const descendantsByValue = new Map<DropdownValue, DropdownValue[]>();

  const visit = (item: NestedGroupItem): DropdownValue[] => {
    if (item.children.length === 0) {
      descendantsByValue.set(item.value, []);

      return [];
    }

    const descendants = item.children.flatMap((child) => {
      const childDescendants = visit(child);
      const isSelectable = selectableByValue?.get(child.value) ?? true;

      // Keep traversal through non-selectable nodes, but only include selectable values in result.
      return isSelectable ? [child.value, ...childDescendants] : childDescendants;
    });

    descendantsByValue.set(item.value, descendants);

    return descendants;
  };

  for (const item of items) {
    visit(item);
  }

  return descendantsByValue;
}

export function buildChildrenByValue(
  items: NestedGroupItem[],
): Map<DropdownValue, DropdownValue[]> {
  const childrenByValue = new Map<DropdownValue, DropdownValue[]>();

  const visit = (item: NestedGroupItem): void => {
    childrenByValue.set(
      item.value,
      item.children.map((child) => child.value),
    );

    for (const child of item.children) {
      visit(child);
    }
  };

  for (const item of items) {
    visit(item);
  }

  return childrenByValue;
}

export function buildParentByValue(
  items: NestedGroupItem[],
): Map<DropdownValue, DropdownValue | null> {
  const parentByValue = new Map<DropdownValue, DropdownValue | null>();

  const visit = (item: NestedGroupItem, parentValue: DropdownValue | null): void => {
    parentByValue.set(item.value, parentValue);

    for (const child of item.children) {
      visit(child, item.value);
    }
  };

  for (const item of items) {
    visit(item, null);
  }

  return parentByValue;
}
